import { ArrowLeftOutlined, CrownTwoTone, FireTwoTone, FlagOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { POSTHOG_EVENTS, posthogTrackView } from '@salescore/client-base'
import type { ProgressType } from '@salescore/client-common'
import type { ViewQueryRecordNode, ViewQueryRecordNodeWithParent } from '@salescore/core'
import { calculateUnit, numberWithDelimiterFilter, numberWithFixedDecimal, unitLabel } from '@salescore/frontend-common'
import { t } from 'i18next'
import type { ReactElement, ReactNode } from 'react'
import { useRecoilValue } from 'recoil'

import type { PivotColumn, PivotRowColumn, RSheetColumn, RSheetRecordNode } from '../../../../../domain/column'
import type { KpiPivotTableContext, KpiTableContext } from '../../../../../recoil/context'
import { kpiParameterAtom, type KpiUserAppearance, kpiUserAppearanceAtom } from '../../../../../recoil/navigation/atoms'
import { viewsContextAtom } from '../../../../../recoil/view/atoms.ts'
import { BACKGROUND_COLOR_SCHEMA_NORMAL, type CellStyle, extractCellStyle } from './extractCellStyle'
import { extractKpiInfo, type KpiInfo } from './extractKpiInfo'
import { extractTransitionInfo, type TransitionInfo } from './extractTransitionInfo'

const DEFAULT_KPI_FONT_SIZE = 18

export const KpiCell = ({
  recordNode,
  column,
  context,
  columns,
  view,
  progress,
}: {
  recordNode: ViewQueryRecordNodeWithParent
  column: PivotRowColumn<ViewQueryRecordNode> | PivotColumn<ViewQueryRecordNode>
  context: KpiTableContext | KpiPivotTableContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
  view: ViewFieldsFragment
  progress: ProgressType
}): ReactElement => {
  const viewsContext = useRecoilValue(viewsContextAtom)
  const kpiUserAppearance = useRecoilValue(kpiUserAppearanceAtom)
  const kpiParameter = useRecoilValue(kpiParameterAtom)
  const kpiInfo = extractKpiInfo(column, recordNode, progress, context)
  const transitionInfo = extractTransitionInfo(kpiInfo, column, columns, recordNode)
  const cellStyle = extractCellStyle(kpiInfo, transitionInfo, kpiUserAppearance, kpiParameter)

  // kpi, rowKpiなどはcellBody側でpaddingを無視している
  const actualText = numberFilter(
    kpiUserAppearance.perElapsedBusinessDays === true ? kpiInfo.actualPerElapsed : kpiInfo.actual,
    kpiInfo,
  )
  const secondaryFontSize = getSecondaryFontSize(kpiUserAppearance, 10)
  return (
    <div
      onClick={() => {
        if (view.type === 'kpiPivot') {
          posthogTrackView(POSTHOG_EVENTS.click_kpi_from_dashboard, {
            viewId: view.id,
            prevViewId: viewsContext.prevView?.id,
            prevViewType: viewsContext.prevView?.type,
            nodeName: undefined,
          })
        }
      }}
      className={`flex size-full items-center justify-center overflow-hidden ${cellStyle.backgroundColor} align-middle `}
    >
      <BackgroundImage cellStyle={cellStyle} />
      <div
        className={`text-center text-xs ${cellStyle.color} cursor-pointer hover:opacity-75`}
        style={{ marginTop: -4 }}
      >
        <div
          className="font-bold leading-7"
          style={{
            marginTop: kpiInfo.goal === undefined ? 2 : 0, // 不要なはずだが、微妙に必要…？
            fontSize: kpiUserAppearance.fontSize ?? getAutoFontSize(actualText.length),
          }}
        >
          {kpiInfo.actual !== undefined && kpiInfo.kpi?.displayFormat === 'yen' && (
            <span style={{ fontSize: secondaryFontSize, marginLeft: 1 }}>¥</span>
          )}
          {actualText}
          <span style={{ fontSize: secondaryFontSize, opacity: 0.6, marginLeft: 2 }}>
            {unitLabel(kpiInfo.actual, kpiInfo.kpi?.unitType, kpiInfo.kpi?.abbreviationType)}
          </span>
          {/* %を追加で表示する */}
          {kpiInfo.kpi?.displayFormat === 'percent' && (
            <span style={{ fontSize: secondaryFontSize, marginLeft: 1 }}>%</span>
          )}
          <ElapsedDays kpiInfo={kpiInfo} />
        </div>
        <GoalRelated kpiInfo={kpiInfo} />
      </div>
      <TransitionValue transitionInfo={transitionInfo} cellStyle={cellStyle} />
    </div>
  )
}

const GoalRelated = ({ kpiInfo }: { kpiInfo: KpiInfo }): ReactElement => {
  const kpiUserAppearance = useRecoilValue(kpiUserAppearanceAtom)
  if (
    kpiInfo.goal === undefined ||
    kpiUserAppearance.goalVisible === false ||
    kpiUserAppearance.perElapsedBusinessDays === true
  ) {
    return <></>
  }

  const secondaryFontSize = getSecondaryFontSize(kpiUserAppearance, 12)
  return (
    <div style={{ fontSize: secondaryFontSize, marginTop: -6 }}>
      / {numberFilter(kpiInfo.goal, kpiInfo)}
      <br />
      {kpiInfo.ratio ?? '-'}%
      {kpiUserAppearance.diffVisible !== false && (
        <div
          className="ml-1 flex justify-center leading-4"
          style={{ fontSize: secondaryFontSize, transform: 'scale(0.9)' }}
        >
          <FlagOutlined className="" style={{ marginRight: 0 }} />
          <div>
            {(kpiInfo.diffActualIdeal ?? 0) > 0 ? '+' : ''}
            {numberFilter(kpiInfo.diffActualIdeal, kpiInfo)}
          </div>
        </div>
      )}
    </div>
  )
}

const TransitionValue = ({
  transitionInfo,
  cellStyle,
}: {
  transitionInfo?: TransitionInfo
  cellStyle: CellStyle
}): ReactElement => {
  if (transitionInfo?.transitionRatio === undefined) {
    return <></>
  }

  return (
    <div
      className="absolute inset-y-0 left-0 m-auto flex h-full items-center justify-center text-center align-middle text-xs"
      style={{
        left: 0,
        transform: 'scale(0.85)',
      }}
    >
      {/* <div style={{ width: 14, height: '70%', backgroundColor: 'white', left: -7 }} className="absolute"></div> */}
      <div className={`absolute inset-x-0 py-2 ${cellStyle.transitionColor} `} style={{ width: '4em', left: '-2em' }}>
        {showRatio(transitionInfo.transitionRatio)}
        %
        <br />
        <ArrowLeftOutlined />
        {transitionInfo.diffTransitionRatio !== undefined && Number.isFinite(transitionInfo.diffTransitionRatio) && (
          <div style={{ transform: 'scale(0.7)' }}>
            <FlagOutlined className="" style={{ marginRight: 0 }} />
            {transitionInfo.diffTransitionRatio > 0 && '+'}
            {numberWithDelimiterFilter(
              Math.abs(transitionInfo.diffTransitionRatio) > 10 ** 7
                ? `${Math.ceil(transitionInfo.diffTransitionRatio / 10 ** 6)}M`
                : // eslint-disable-next-line sonarjs/no-nested-conditional
                  Math.abs(transitionInfo.diffTransitionRatio) > 10 ** 4
                  ? `${Math.ceil(transitionInfo.diffTransitionRatio / 10 ** 3)}K`
                  : `${transitionInfo.diffTransitionRatio}`,
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const ElapsedDays = ({ kpiInfo }: { kpiInfo: KpiInfo }): ReactElement => {
  const kpiUserAppearance = useRecoilValue(kpiUserAppearanceAtom)
  const { elapsedDays, actual } = kpiInfo
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (kpiUserAppearance.perElapsedBusinessDays !== true || elapsedDays === undefined) {
    return <></>
  }

  return (
    <div
      className="ml-1 mt-1 flex justify-center leading-4 text-gray-300"
      style={{ fontSize: getSecondaryFontSize(kpiUserAppearance, 12), transform: 'scale(0.9)' }}
    >
      <div>
        {elapsedDays} {t(`営業日`)}
        <br />
        {t(`合計`)}:{numberFilter(actual, kpiInfo)}
      </div>
    </div>
  )
}

const getAutoFontSize = (textLength: number): number => {
  // いったん、widthによらず一定にする
  if (textLength >= 12) {
    return 10
  }
  if (textLength >= 11) {
    return 11
  }
  if (textLength >= 10) {
    return 12
  }
  if (textLength >= 9) {
    return 13
  }
  if (textLength >= 8) {
    return 14
  }
  if (textLength >= 7) {
    return 15
  }
  return DEFAULT_KPI_FONT_SIZE
}

const getSecondaryFontSize = (kpiUserAppearance: KpiUserAppearance, defaultFontSize: number): number => {
  if (kpiUserAppearance.fontSize === undefined) {
    return defaultFontSize
  }
  const fontSizeRatio = defaultFontSize / DEFAULT_KPI_FONT_SIZE
  return kpiUserAppearance.fontSize * fontSizeRatio
}

const showRatio = (x: number | undefined): string => {
  if (x === undefined || !Number.isFinite(x)) {
    return `-`
  }
  if (Math.abs(x) >= 1000) {
    return '999+'
  }
  if (Math.abs(x) < 10) {
    return x.toPrecision(2) // extractTransitionInfoで2桁で丸めているが、4.0が4になってしまっているため、ここで改めて2桁に合わせる
  }

  return `${x}`
}

const numberFilter = (x: number | undefined, kpi: KpiInfo): string => {
  if (x === undefined) {
    return '-'
  }

  const calculatedNumber = calculateUnit(x, kpi.kpi?.unitType, kpi.kpi?.abbreviationType)
  const fixedNumber = numberWithFixedDecimal(calculatedNumber, kpi.kpi?.decimalPlaceType, kpi.kpi?.abbreviationType)

  return numberWithDelimiterFilter(fixedNumber)
}

const BackgroundImage = ({ cellStyle }: { cellStyle: CellStyle }): ReactNode => {
  if (cellStyle.backgroundColor === BACKGROUND_COLOR_SCHEMA_NORMAL.danger) {
    return (
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <FireTwoTone
          twoToneColor="#920202ff"
          style={{
            fontSize: '200%',
            transform: 'scale(1.5)',
          }}
        />
      </div>
    )
  }
  if (cellStyle.backgroundColor === BACKGROUND_COLOR_SCHEMA_NORMAL.success) {
    return (
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.08]">
        <CrownTwoTone
          style={{
            fontSize: '200%',
            transform: 'scale(1.5)',
          }}
        />
      </div>
    )
  }
  return null
}
